import { memo, useMemo } from 'react';
import type { ReactNode } from 'react';
import { Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';

import { PLAN_COLORS } from '../constants';
import type { DailyPlan, PlanConfig } from '../types';

/** Matches reference HTML CSS variables (--surface2, --text3, --border, etc.) */
const DV = {
  navy: '#0f1623',
  text2: '#4a5568',
  text3: '#718096',
  border: '#e2e8f0',
  border2: '#cbd5e0',
  surface: '#ffffff',
  surface2: '#f7f9fc',
  surface3: '#eef2f8',
  shadow: '0 1px 3px rgba(0,0,0,.06), 0 1px 2px rgba(0,0,0,.04)',
  radiusLg: 12,
} as const;

const tableSx = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: 12,
  bgcolor: DV.surface,
  borderRadius: `${DV.radiusLg}px`,
  overflow: 'hidden',
  boxShadow: DV.shadow,
  '& .MuiTableCell-root': {
    borderBottom: `1px solid ${DV.border}`,
    fontFamily: 'JetBrains Mono, monospace',
    fontSize: 11,
    py: 0.75,
    px: 1.5,
  },
  '& thead .MuiTableCell-root': {
    bgcolor: DV.surface2,
    fontFamily: 'Plus Jakarta Sans, sans-serif',
    fontSize: 11,
    fontWeight: 600,
    color: DV.text3,
    whiteSpace: 'nowrap',
    py: 1,
  },
  '& tbody tr:last-child .MuiTableCell-root': {
    borderBottom: 'none',
  },
  '& tbody tr:hover .MuiTableCell-root': {
    bgcolor: DV.surface2,
  },
};

const formatWindowEvents = (events: string[]) => {
  const labels: Record<string, string> = {
    'ramp end': '★ ramp end',
    'new leads': '⬤ new leads',
    '+ address': '+ address',
  };
  return events.map((e) => labels[e] ?? e).join(' · ');
};

const utilColor = (utilPct: number) => {
  if (utilPct >= 95) return PLAN_COLORS.red;
  if (utilPct >= 70) return PLAN_COLORS.newBatch;
  return PLAN_COLORS.green;
};

const formatCount = (n: number) => (n > 0 ? n.toLocaleString() : '—');

interface DailyViewTabProps {
  dailyPlan?: DailyPlan;
  config: PlanConfig;
  dailyBatch?: number;
  horizon: number;
}

const DailyViewTabInner = ({ dailyPlan, config, dailyBatch, horizon }: DailyViewTabProps) => {
  const ramp = config.ramp;
  const batchSize = dailyBatch ?? Math.ceil(config.total / Math.max(ramp, 1));

  const days = useMemo(
    () => (dailyPlan?.days ?? []).filter((d) => d.day <= horizon),
    [dailyPlan, horizon],
  );

  const windows = useMemo(
    () => (dailyPlan?.windows ?? []).filter((w) => w.windowStart <= horizon),
    [dailyPlan, horizon],
  );

  const maxCumLeads = days[days.length - 1]?.cumLeads ?? 1;
  const maxCumEmails = days[days.length - 1]?.cumEmails ?? 1;
  const maxFleetCap = days[days.length - 1]?.fleetCap ?? 1;

  if (!dailyPlan?.days?.length) {
    return (
      <Box sx={{ fontSize: 13, color: DV.text3 }}>
        Generate the plan to view the daily breakdown.
      </Box>
    );
  }

  return (
    <Box>
      {/* Cumulative 10-day table */}
      <Box sx={{ mb: 2.5 }}>
        <Box
          sx={{
            fontSize: 11,
            fontWeight: 700,
            color: DV.text3,
            letterSpacing: '0.07em',
            textTransform: 'uppercase',
            mb: 1.25,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            fontFamily: 'Plus Jakarta Sans, sans-serif',
          }}
        >
          Cumulative totals — every 10 days
          <Box sx={{ flex: 1, height: '1px', bgcolor: DV.border }} />
        </Box>
        <TableContainer sx={{ borderRadius: `${DV.radiusLg}px`, overflow: 'hidden' }}>
          <Table size="small" sx={tableSx}>
            <TableHead>
              <TableRow>
                <TableCell>Period</TableCell>
                <TableCell>
                  Leads loaded
                  <Box component="span" sx={{ display: 'block', fontWeight: 400, fontSize: 10 }}>
                    this window
                  </Box>
                </TableCell>
                <TableCell>
                  Cumulative leads
                  <Box component="span" sx={{ display: 'block', fontWeight: 400, fontSize: 10 }}>
                    since day 1
                  </Box>
                </TableCell>
                <TableCell>
                  Email slots allocated
                  <Box component="span" sx={{ display: 'block', fontWeight: 400, fontSize: 10 }}>
                    this window
                  </Box>
                </TableCell>
                <TableCell>
                  Cumulative slots allocated
                  <Box component="span" sx={{ display: 'block', fontWeight: 400, fontSize: 10 }}>
                    since day 1
                  </Box>
                </TableCell>
                <TableCell>
                  Avg capacity
                  <Box component="span" sx={{ display: 'block', fontWeight: 400, fontSize: 10 }}>
                    emails/day
                  </Box>
                </TableCell>
                <TableCell sx={{ minWidth: 140 }}>Progress</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {windows.map((w) => {
                const isEventWindow = w.events.length > 0;
                const leadsBarPct = Math.round((w.cumLeads / maxCumLeads) * 100);
                const emailsBarPct = Math.round((w.cumSlots / maxCumEmails) * 100);
                const eventBadge =
                  w.events.length > 0
                    ? formatWindowEvents(w.events)
                    : '';

                return (
                  <TableRow
                    key={w.windowStart}
                    sx={isEventWindow ? { '& .MuiTableCell-root': { bgcolor: PLAN_COLORS.bothBg } } : undefined}
                  >
                    <TableCell
                      sx={{
                        color: DV.text2,
                        fontWeight: 500,
                        fontFamily: 'Plus Jakarta Sans, sans-serif',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Days {w.windowStart}–{w.windowEnd}
                      {eventBadge && (
                        <Box
                          component="span"
                          sx={{
                            fontSize: 10,
                            color: PLAN_COLORS.both,
                            fontFamily: 'Plus Jakarta Sans, sans-serif',
                            fontWeight: 500,
                            ml: 0.5,
                          }}
                        >
                          {eventBadge}
                        </Box>
                      )}
                    </TableCell>
                    <TableCell sx={{ color: PLAN_COLORS.past, fontWeight: 600 }} className="leads-col">
                      {w.windowLeads.toLocaleString()}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, color: PLAN_COLORS.past }}>
                      {w.cumLeads.toLocaleString()}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: PLAN_COLORS.green }}>
                      {w.windowSlots.toLocaleString()}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, color: PLAN_COLORS.green }}>
                      {w.cumSlots.toLocaleString()}
                    </TableCell>
                    <TableCell sx={{ color: PLAN_COLORS.orange }}>
                      {w.avgCap.toLocaleString()}
                    </TableCell>
                    <TableCell sx={{ py: 0.75, pr: 1.5, pl: 1.5 }}>
                      <Box sx={{ mb: 0.375 }}>
                        <Box sx={{ fontSize: 9, color: DV.text3, mb: 0.125, fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                          Leads {leadsBarPct}%
                        </Box>
                        <Box
                          sx={{
                            height: 5,
                            bgcolor: DV.surface3,
                            borderRadius: 3,
                            overflow: 'hidden',
                          }}
                        >
                          <Box
                            sx={{
                              height: '100%',
                              width: `${leadsBarPct}%`,
                              bgcolor: PLAN_COLORS.past,
                              borderRadius: 3,
                              opacity: 0.7,
                            }}
                          />
                        </Box>
                      </Box>
                      <Box>
                        <Box sx={{ fontSize: 9, color: DV.text3, mb: 0.125, fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                          Slots {emailsBarPct}%
                        </Box>
                        <Box
                          sx={{
                            height: 5,
                            bgcolor: DV.surface3,
                            borderRadius: 3,
                            overflow: 'hidden',
                          }}
                        >
                          <Box
                            sx={{
                              height: '100%',
                              width: `${emailsBarPct}%`,
                              bgcolor: PLAN_COLORS.green,
                              borderRadius: 3,
                              opacity: 0.7,
                            }}
                          />
                        </Box>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Daily detail table */}
      <Box
        sx={{
          fontSize: 11,
          fontWeight: 700,
          color: DV.text3,
          letterSpacing: '0.07em',
          textTransform: 'uppercase',
          mb: 1.25,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          fontFamily: 'Plus Jakarta Sans, sans-serif',
        }}
      >
        Daily detail
        <Box sx={{ flex: 1, height: '1px', bgcolor: DV.border }} />
      </Box>

      <TableContainer sx={{ borderRadius: `${DV.radiusLg}px`, overflow: 'hidden' }}>
        <Table size="small" sx={tableSx}>
          <TableHead>
            <TableRow>
              <TableCell>Day</TableCell>
              <TableCell>Leads loaded</TableCell>
              <TableCell>Cumul. leads</TableCell>
              <TableCell>Onboard emails</TableCell>
              <TableCell>Follow-up emails</TableCell>
              <TableCell>Total demand</TableCell>
              <TableCell>Fleet capacity</TableCell>
              <TableCell>Usable (80%)</TableCell>
              <TableCell>Slots allocated</TableCell>
              <TableCell>Cumul. slots</TableCell>
              <TableCell>Addrs</TableCell>
              <TableCell sx={{ minWidth: 100 }}>Bar</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(() => {
              let lastSection = '';
              const rows: ReactNode[] = [];

              days.forEach((d) => {
                let section = '';
                if (d.day === 1) section = 'Ramp-up';
                else if (d.day === ramp + 1) section = 'Post-ramp';
                else if (d.day === 181) section = 'Month 7+';

                if (section && section !== lastSection) {
                  lastSection = section;
                  rows.push(
                    <TableRow key={`sec-${section}`}>
                      <TableCell
                        colSpan={12}
                        sx={{
                          bgcolor: `${DV.surface2} !important`,
                          color: DV.text3,
                          fontSize: 10,
                          letterSpacing: '0.06em',
                          textTransform: 'uppercase',
                          fontWeight: 600,
                          py: 0.5,
                          fontFamily: 'Plus Jakarta Sans, sans-serif',
                        }}
                      >
                        {section}
                      </TableCell>
                    </TableRow>,
                  );
                }

                const eventRow = d.isNewStart || d.isAddrDay || d.isRampEnd;
                const barPct = Math.min(100, Math.round((d.fleetCap / Math.max(maxFleetCap, 1)) * 100));
                const loadBarPct =
                  d.onboardToday > 0
                    ? Math.min(100, Math.round((d.onboardToday / Math.max(batchSize, 1)) * 100))
                    : 0;
                const slotColor = utilColor(d.utilPct);

                rows.push(
                  <TableRow
                    key={d.day}
                    sx={
                      eventRow
                        ? {
                            '& .MuiTableCell-root': {
                              bgcolor: `${PLAN_COLORS.bothBg} !important`,
                              color: PLAN_COLORS.both,
                              fontWeight: 600,
                            },
                          }
                        : undefined
                    }
                  >
                    <TableCell
                      sx={{
                        color: eventRow ? PLAN_COLORS.both : DV.text2,
                        fontWeight: 500,
                        fontFamily: 'Plus Jakarta Sans, sans-serif',
                      }}
                    >
                      Day {d.day}
                      {d.isRampEnd ? ' ★' : ''}
                      {d.isNewStart ? ' ⬤' : ''}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: PLAN_COLORS.past, fontFamily: 'JetBrains Mono, monospace' }}>
                      {formatCount(d.onboardToday)}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: PLAN_COLORS.past }}>
                      {d.cumLeads.toLocaleString()}
                    </TableCell>
                    <TableCell sx={{ color: PLAN_COLORS.orange }}>
                      {formatCount(d.onboardToday)}
                    </TableCell>
                    <TableCell sx={{ color: DV.text2 }}>
                      {formatCount(d.followUpToday)}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, color: DV.navy, fontFamily: 'JetBrains Mono, monospace' }}>
                      {d.demandToday.toLocaleString()}
                    </TableCell>
                    <TableCell sx={{ color: DV.text3 }}>
                      {d.fleetCap.toLocaleString()}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: PLAN_COLORS.orange }}>
                      {d.usableCap.toLocaleString()}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: slotColor }}>
                      {d.slotsAllocated.toLocaleString()}
                      <Box
                        component="span"
                        sx={{ fontSize: 10, fontWeight: 400, opacity: 0.7, ml: 0.25 }}
                      >
                        ({d.utilPct}%)
                      </Box>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: PLAN_COLORS.green }}>
                      {d.cumEmails.toLocaleString()}
                    </TableCell>
                    <TableCell sx={{ color: DV.text3, fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                      {d.addrCount}
                    </TableCell>
                    <TableCell sx={{ py: 0.75, pr: 1.5, pl: 0 }}>
                      <Box
                        sx={{
                          position: 'relative',
                          height: 12,
                          bgcolor: DV.surface3,
                          borderRadius: 3,
                          overflow: 'hidden',
                        }}
                      >
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            height: '100%',
                            width: `${barPct}%`,
                            bgcolor: PLAN_COLORS.pastBg,
                            borderRadius: 3,
                          }}
                        />
                        {loadBarPct > 0 && (
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 3,
                              left: 0,
                              height: 6,
                              width: `${loadBarPct}%`,
                              bgcolor: PLAN_COLORS.orange,
                              borderRadius: 2,
                              opacity: 0.85,
                            }}
                          />
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>,
                );
              });

              return rows;
            })()}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Legend — .legend-row */}
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          mt: 1.5,
          fontSize: 11,
          color: DV.text2,
          flexWrap: 'wrap',
          fontFamily: 'Plus Jakarta Sans, sans-serif',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.625 }}>
          <Box sx={{ width: 10, height: 10, borderRadius: 0.5, bgcolor: PLAN_COLORS.past, flexShrink: 0 }} />
          Cumulative leads (blue)
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.625 }}>
          <Box sx={{ width: 10, height: 10, borderRadius: 0.5, bgcolor: PLAN_COLORS.orange, flexShrink: 0 }} />
          Usable capacity = fleet × 80% (orange)
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.625 }}>
          <Box sx={{ width: 10, height: 10, borderRadius: 0.5, bgcolor: PLAN_COLORS.green, flexShrink: 0 }} />
          Cumulative slots allocated (green)
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.625 }}>
          <Box sx={{ width: 10, height: 10, borderRadius: 0.5, bgcolor: PLAN_COLORS.newBatch, flexShrink: 0 }} />
          Slots at 70–94% of usable
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.625 }}>
          <Box sx={{ width: 10, height: 10, borderRadius: 0.5, bgcolor: PLAN_COLORS.red, flexShrink: 0 }} />
          Slots at ≥95% of usable
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.625 }}>
          <Box sx={{ fontSize: 12 }}>★</Box>
          Ramp end&nbsp;·&nbsp;
          <Box sx={{ fontSize: 12 }}>⬤</Box>
          New leads start
        </Box>
      </Box>
    </Box>
  );
};

/** Skip re-render while editing left-panel config — daily data only changes after Generate. */
export const DailyViewTab = memo(DailyViewTabInner, (prev, next) => {
  return (
    prev.dailyPlan === next.dailyPlan &&
    prev.dailyBatch === next.dailyBatch &&
    prev.horizon === next.horizon &&
    prev.config.ramp === next.config.ramp &&
    prev.config.newStart === next.config.newStart
  );
});
